import React, { memo, useState, useCallback, Fragment, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { SdfBusyIndicator } from '@waypoint/react-components';
import Resources from './resources';

import {
  StyledAppBar, LogoContainer, LogoImage, NavButton, SmartSearchButton, NavSection, ActionsContainer, ContactNavButton, StyledStarIcon, SmartSearchText, MobileHeaderBox, DesktopHeaderBox,
  StyledSearchForm, SearchIconButton, DesktopSearchTray, DesktopSearchTrayContainer, HeaderContentContainer,
  AISolutionsBadge, DesktopToolbar, DesktopHeaderContent
} from './styles';
import CountrySelector from './CountrySelector';
import PropTypes from 'prop-types';
import { MarketplaceDropdown } from './MarketplaceDropdown';
import MobileHeader from './MobileHeader';
import GlobalSearch from './GlobalSearch';
import NotificationItem from './notificationItem';
import { setActiveTab, useGetActiveCartQuery, useGetUserEntitlementsQuery } from '@headless-shell/store';
import { breakpoints } from '@headless-shell/shared-styles';
import { SdfButton } from '@waypoint/react-components';
import { buildLegacyLoginContinueUrl, getStoreUrl, handleProductClick as productDetailsNavigate } from '../../utils/helper';
import SmartSearchApp from '../remotes/smartSearchApp';
import { useTranslatedObject } from '../../utils/translation';
import AdpLogo from '../../assets/AdpLogo.svg?url';
import {
  AdpLogoClick, HeaderAboutClick, HeaderContactUsTrigger, HeaderAISolutionsClick,
  HeaderLoginInitiate, HeaderMenu, HeaderSearchInitiate, HeaderSmartSearchInitiate, sendGA4Data
} from '../../analytics/events';
import { openSmartSearchOnLogin, requiresSso } from '../../utils/auth';
import { CONFIG } from '../../utils/config';
import LoginBanner from '../LoginBanner';
import { useSpaRouteEvents } from '../../hooks/useSpaRouteEvents';

export const SharedHeader = memo(({ authenticated, navigate, sessionCode, isContactOpen, setIsContactOpen, onContactUsClick }) => {
  const dispatch = useDispatch();
  const activeTab = useSelector(state => state.app.activeTab);

  // show header cart only when headless checkout is disabled
  const { data: cartData, isLoading: isCartLoading, isError: isCartError } = useGetActiveCartQuery(undefined, {
    skip: !authenticated || CONFIG.ENABLE_HEADLESS_CHECKOUT,
  });
  const { locale = '' } = useSelector(state => state.app) || {};
  const userContext = useSelector(state => state?.userContext) || {};
  const [isSmartSearchOpen, setIsSmartSearchOpen] = useState(false);
  const [isTabletSearchOpen, setIsTabletSearchOpen] = useState(false);
  const theme = useTheme();
  const header = useTranslatedObject('header');
  const isDesktopSearchViewport = useMediaQuery(`(min-width: ${breakpoints.lg})`);
  const isTabletSearchViewport = useMediaQuery(theme.breakpoints.up('md')) && !isDesktopSearchViewport;
  const closeTabletSearch = () => setIsTabletSearchOpen(false);
  const { allowedResources = [] } = useGetUserEntitlementsQuery(undefined, {
    skip: !authenticated,
    selectFromResult: ({ data }) => ({ allowedResources: data?.allowedResources || [] })
  });
  const leftNav = (header.left_nav || []).filter(item =>
    item.key !== 'solutions' || !allowedResources.includes('solutions')
  );
  const smartSearchAccess = allowedResources.includes('smart_search');
  const isSsoInProgress = sessionCode !== 'LEGACY' && requiresSso(sessionCode);


  useEffect(() => {
    if (!isTabletSearchViewport && isTabletSearchOpen) {
      closeTabletSearch();
    }
  }, [isTabletSearchOpen, isTabletSearchViewport]);

  const openSmartSearchFromQueryParam = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openSmartSearch') !== 'true') return;
    setIsSmartSearchOpen(true);
    sendGA4Data(HeaderSmartSearchInitiate);
    openSmartSearchOnLogin(urlParams);
  }, []);

  useSpaRouteEvents(openSmartSearchFromQueryParam, { enabled: !!authenticated });

const handleContactClick = (e, cta) => {
  e.preventDefault();
  closeTabletSearch();
  onContactUsClick();
  sendGA4Data(HeaderContactUsTrigger, { eventLabel: cta });
};

const handleContactUsClick = useCallback((appId, appName) => {
  console.log('*** Smart Search Contact Us clicked:', appId, appName);
  onContactUsClick(appId, appName);
  setIsSmartSearchOpen(false);
}, [onContactUsClick]);


const handleProductClick =(app, tab, position, conversationId) => {
  console.log('*** Product clicked:', app.id);
  const mappedTab = tab === 'editionPricing' ? 'editions' : tab;
  productDetailsNavigate({ product: app, navigate, tab: mappedTab, event: 'smart_search_product_click'});
}

  const handleClick = (tab) => {
    switch (tab) {
      case 'about': {
        const aboutUrl = getStoreUrl(locale, tab);
        window.open(buildLegacyLoginContinueUrl(aboutUrl, authenticated), '_blank');
        dispatch(setActiveTab(tab));
        sendGA4Data(HeaderAboutClick);
        break;
      }
      case 'apps_home':
        navigate('/home');
        break;
      case 'solutions':
        if (tab === activeTab) {
          dispatch(setActiveTab(''));
          break;
        }
        dispatch(setActiveTab(tab));
        sendGA4Data(HeaderMenu, { eventLabel: "Solutions" });
        break;
      case 'ai_solutions': {
        const aiSolutionsUrl = getStoreUrl(locale, tab);
        navigate(aiSolutionsUrl)
        dispatch(setActiveTab(tab));
        sendGA4Data(HeaderAISolutionsClick);
        break;
      }
      case 'smart_search':
        if (!authenticated) {
          // Redirect to login and reopen smart search on the shell root route.
          const returnUrl = `/?openSmartSearch=true`;
          navigate(`/login?continue=${encodeURIComponent(returnUrl)}`);
          sendGA4Data(HeaderSmartSearchInitiate);
        } else {
          setIsSmartSearchOpen(true);
          sendGA4Data(HeaderSmartSearchInitiate);
        }
        break;
      default:
        dispatch(setActiveTab(tab));
        break;
    }
    closeTabletSearch();
  };

  const onLogoClick = () => {
    sendGA4Data(AdpLogoClick);
  };

  const onLoginClick = () => {
    closeTabletSearch();
    sendGA4Data(HeaderLoginInitiate);
    const returnUrl = window.location.pathname + window.location.search;
    window.dispatchEvent(new CustomEvent('auth-request-login', { detail: { returnUrl } }));
    dispatch(setActiveTab(''));
  };

  const handleTabletSearchToggle = () => {
    setIsTabletSearchOpen((previouslyOpen) => {
      const nextOpen = !previouslyOpen;

      if (nextOpen) {
        sendGA4Data(HeaderSearchInitiate);
      }

      return nextOpen;
    });
  };
const handleSolutionsClick = () => {
  console.log('*** Solutions clicked');
}

  const renderNavLabel = (item) => {
    if (item.isNew) {
      return (
        <Fragment>
          {item.label}
          <AISolutionsBadge>New!</AISolutionsBadge>
        </Fragment>
      );
    }
    return item.label;
  };

  return (
     <React.Fragment>

         {authenticated === false && <LoginBanner />}
      {/* Mobile Header */}
      <MobileHeaderBox>
        <MobileHeader
          authenticated={authenticated}
          navigate={navigate}
          isContactOpen={isContactOpen}
          setIsContactOpen={setIsContactOpen}
          renderNavLabel={renderNavLabel}
          onAboutClick={handleClick}
          cartData={cartData}
          isCartLoading={isCartLoading}
          isCartError={isCartError}
        />
      </MobileHeaderBox>

      {/* Desktop Header */}
      <DesktopHeaderBox id="desktop-header">
        <StyledAppBar id="desktop-app-bar" position="fixed" loggedIn={authenticated}>
          <HeaderContentContainer maxWidth={false}>
            <DesktopHeaderContent>
              <DesktopToolbar disableGutters>
                {/* Logo Section */}
                <LogoContainer
                  aria-label="adp-logo"
                  id="desktop-logo-container"
                  component="a"
                  href={CONFIG.POST_LOGIN_REDIRECT_URI || '/'}
                  onClick={onLogoClick}
                >
                  <LogoImage
                    id="desktop-logo"
                    src={AdpLogo}
                    aria-label="adp-logo"
                    alt="ADP Marketplace"
                  />
                </LogoContainer>

                {/* Navigation Section */}
                <NavSection id="desktop-nav-section">
                  {leftNav?.map(item => {
                    if (item.key === 'smart_search') {
                      return (
                        <SmartSearchButton
                          key={item.key}
                          active={activeTab === item.key}
                          id="desktop-smart-search"
                          onClick={() => handleClick(item.key)}
                        >
                          <StyledStarIcon />
                          <SmartSearchText component="span">
                            {item.label}
                          </SmartSearchText>
                        </SmartSearchButton>
                      );
                    }

                    return (
                      !item.mobileOnly && (
                        <Fragment key={item.key}>
                          <NavButton
                            id={`desktop-nav-${item.key}`}
                            active={activeTab === item.key}
                            onClick={() => handleClick(item.key)}
                          >
                            {renderNavLabel(item)}
                          </NavButton>
                        </Fragment>
                      )
                    );
                  })}
                </NavSection>

                {/* Search and Actions Section */}
                <ActionsContainer id="desktop-actions-container">
                <StyledSearchForm>
                    <GlobalSearch navigate={navigate} width="w-72" isAuthenticated={authenticated} onClose={() => dispatch(setActiveTab(''))} />
                </StyledSearchForm>

                <SearchIconButton
                  id="desktop-search-toggle"
                  aria-label="Open search"
                  active={isTabletSearchOpen}
                  $desktopOnly
                  onClick={handleTabletSearchToggle}
                >
                  <SearchIcon />
                </SearchIconButton>

                  {/* Authenticated user actions */}
                  {authenticated && (
                    <NotificationItem 
                      cartData={cartData}
                      isCartLoading={isCartLoading}
                      isCartError={isCartError}
                      navigate={navigate}
                    />
                  )}
                  <CountrySelector navigate={navigate} />

                  <ContactNavButton
                    id="desktop-contact-button"
                    active={activeTab === header?.contact}
                    onClick={(e) => handleContactClick(e, header?.contact)}
                  >
                    {header.contact}
                  </ContactNavButton>

                  {/* Authentication Section */}
                  {authenticated ? (
                    <Resources navigate={navigate} />
                  ) : (
                    (authenticated === false || isSsoInProgress) && (
                      <div className="relative inline-block">
                        {isSsoInProgress && (
                          authenticated === false ? (
                            <SdfBusyIndicator label-aria="Checking session..." overlay size="sm" />
                          ) : (
                            <SdfBusyIndicator label-aria="Checking session..." size="sm" />
                          )
                        )}
                        {authenticated === false && (
                          <SdfButton
                            id="desktop-login-button"
                            emphasis="primary"
                            onClick={onLoginClick}
                          >
                            {header.login}
                          </SdfButton>
                        )}
                      </div>
                    )
                  )}
                </ActionsContainer>
              </DesktopToolbar>
              <MarketplaceDropdown
                solutions="solutions"
                navigate={navigate}
                locale={locale}
                authenticated={authenticated}
              />
            </DesktopHeaderContent>
          </HeaderContentContainer>
        </StyledAppBar>

        {isTabletSearchOpen && (
          <DesktopSearchTray loggedIn={authenticated}>
            <DesktopSearchTrayContainer maxWidth={false}>
              <GlobalSearch
                navigate={navigate}
                onSearchComplete={closeTabletSearch}
                isAuthenticated={authenticated}
              />
            </DesktopSearchTrayContainer>
          </DesktopSearchTray>
        )}
      </DesktopHeaderBox>
      <SmartSearchApp
        onContactUsClick={handleContactUsClick}
        onProductClick={handleProductClick}
        onSolutionsClick={handleSolutionsClick}
        open={isSmartSearchOpen}
        onClose={() => setIsSmartSearchOpen(false)}
        authenticated={authenticated}
        userContext={userContext}
        smartSearchAccess={smartSearchAccess}
        navigate={navigate}
      />
    </React.Fragment>
  );
});

SharedHeader.displayName = 'SharedHeader';

SharedHeader.propTypes = {
  authenticated: PropTypes.bool,
  navigate: PropTypes.func.isRequired,
  sessionCode: PropTypes.string,
  isFirstLegacy: PropTypes.bool,
  isContactOpen: PropTypes.bool,
  setIsContactOpen: PropTypes.func.isRequired,
  onContactUsClick: PropTypes.func.isRequired,
};
